/*###########################################################################
 // Library card holo tilt + click-to-enlarge
 // Inspired by https://poke-holo.simey.me/
 //#########################################################################*/

function clamp(value, min = 0, max = 100) {
  return Math.min(max, Math.max(min, value))
}

function round(value, precision = 2) {
  const p = 10 ** precision
  return Math.round(value * p) / p
}

function adjust(value, fromMin, fromMax, toMin, toMax) {
  return round(
    toMin + ((toMax - toMin) * (value - fromMin)) / (fromMax - fromMin),
  )
}

export function registerChaseHoloCard(Alpine) {
  Alpine.data('chaseHoloCard', () => ({
    interacting: false,
    active: false,
    pointerX: 50,
    pointerY: 50,
    pointerFromCenter: 0,
    opacity: 0,
    rotateX: 0,
    rotateY: 0,
    backgroundX: 50,
    backgroundY: 50,
    scale: 1,
    translateX: 0,
    translateY: 0,
    rotateDeltaX: 0,
    firstPop: true,
    _raf: null,
    _pending: null,
    _leaveTimer: null,
    _spinTimer: null,
    _dragMoved: false,
    _pointerStart: null,
    _onKey: null,
    _onResize: null,

    get styleVars() {
      return {
        '--pointer-x': `${this.pointerX}%`,
        '--pointer-y': `${this.pointerY}%`,
        '--pointer-from-center': String(this.pointerFromCenter),
        '--card-opacity': String(this.opacity),
        '--rotate-x': `${this.rotateX + this.rotateDeltaX}deg`,
        '--rotate-y': `${this.rotateY}deg`,
        '--background-x': `${this.backgroundX}%`,
        '--background-y': `${this.backgroundY}%`,
        '--card-scale': String(this.scale),
        '--translate-x': `${this.translateX}px`,
        '--translate-y': `${this.translateY}px`,
      }
    },

    init() {
      this._onKey = (event) => {
        if (event.key === 'Escape' && this.active) this.deactivate()
      }
      this._onResize = () => {
        if (this.active) this.recenter()
      }
      window.addEventListener('keydown', this._onKey)
      window.addEventListener('resize', this._onResize)
    },

    destroy() {
      window.removeEventListener('keydown', this._onKey)
      window.removeEventListener('resize', this._onResize)
      clearTimeout(this._leaveTimer)
      clearTimeout(this._spinTimer)
      if (this.active) window.app?.enableScroll?.()
    },

    onPointerDown(event) {
      this._dragMoved = false
      this._pointerStart = { x: event.clientX, y: event.clientY }
    },

    onPointerMove(event) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

      if (this._pointerStart) {
        const dx = event.clientX - this._pointerStart.x
        const dy = event.clientY - this._pointerStart.y
        if (Math.hypot(dx, dy) > 8) this._dragMoved = true
      }

      clearTimeout(this._leaveTimer)
      this.interacting = true

      const el = event.currentTarget
      const rect = el.getBoundingClientRect()
      const absolute = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      }
      const percent = {
        x: clamp(round((100 / rect.width) * absolute.x)),
        y: clamp(round((100 / rect.height) * absolute.y)),
      }
      const center = {
        x: percent.x - 50,
        y: percent.y - 50,
      }

      this._pending = {
        pointerX: percent.x,
        pointerY: percent.y,
        pointerFromCenter: clamp(
          Math.sqrt(center.x * center.x + center.y * center.y) / 50,
          0,
          1,
        ),
        opacity: 1,
        rotateX: round(-(center.x / 3.5)),
        rotateY: round(center.y / 3.5),
        backgroundX: adjust(percent.x, 0, 100, 37, 63),
        backgroundY: adjust(percent.y, 0, 100, 33, 67),
      }

      if (this._raf == null) {
        this._raf = requestAnimationFrame(() => {
          if (this._pending) {
            Object.assign(this, this._pending)
            this._pending = null
          }
          this._raf = null
        })
      }
    },

    onPointerLeave() {
      if (this.active) return
      clearTimeout(this._leaveTimer)
      this._leaveTimer = setTimeout(() => this.resetTilt(), 120)
    },

    onPointerUp(event) {
      const start = this._pointerStart
      this._pointerStart = null
      if (!start) return
      if (this._dragMoved) return
      // Only treat primary button / touch as a click-to-expand.
      if (event.button != null && event.button !== 0) return
      this.toggleActive()
    },

    toggleActive() {
      if (this.active) this.deactivate()
      else this.activate()
    },

    activate() {
      if (this.active) return
      this.active = true
      this.interacting = true
      this.opacity = Math.max(this.opacity, 0.85)
      window.app?.disableScroll?.()
      this.$nextTick(() => {
        this.recenter()
        if (this.firstPop) {
          this.firstPop = false
          this.playIntroSpin()
        }
      })
    },

    deactivate() {
      if (!this.active) return
      this.active = false
      this.scale = 1
      this.translateX = 0
      this.translateY = 0
      this.rotateDeltaX = 0
      clearTimeout(this._spinTimer)
      window.app?.enableScroll?.()
      this.resetTilt()
    },

    recenter() {
      const el = this.$el
      if (!el) return
      const rect = el.getBoundingClientRect()
      const viewW = window.innerWidth
      const viewH = window.innerHeight
      const scaleW = (viewW / rect.width) * 0.72
      const scaleH = (viewH / rect.height) * 0.82
      this.scale = Math.min(scaleW, scaleH, 1.85)
      this.translateX = round(viewW / 2 - (rect.left + rect.width / 2))
      this.translateY = round(viewH / 2 - (rect.top + rect.height / 2))
    },

    playIntroSpin() {
      this.rotateDeltaX = 0
      this.$el?.classList.add('is-spinning')
      requestAnimationFrame(() => {
        this.rotateDeltaX = 360
        clearTimeout(this._spinTimer)
        this._spinTimer = setTimeout(() => {
          // Snap without a visible jump — 360deg ≡ 0deg.
          this.$el?.classList.add('is-spin-snap')
          this.$el?.classList.remove('is-spinning')
          this.rotateDeltaX = 0
          requestAnimationFrame(() => {
            this.$el?.classList.remove('is-spin-snap')
          })
        }, 900)
      })
    },

    resetTilt() {
      this.interacting = false
      this.pointerX = 50
      this.pointerY = 50
      this.pointerFromCenter = 0
      this.opacity = 0
      this.rotateX = 0
      this.rotateY = 0
      this.backgroundX = 50
      this.backgroundY = 50
    },
  }))
}
